import React, {useEffect, useRef, useState} from "react";
import {
    DialogTitle,
    DialogContent,
    Grid2 as Grid,
    FormControl,
    TextField,
    DialogActions,
    Button,
    Dialog,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Link,
    FormGroup,
    Checkbox
} from "@mui/material";
import {DatePicker, TimePicker} from "@mui/x-date-pickers";
import CustomAutocomplete from "./CustomAutocomplete";
import {
    RESULT_GROUP_WORKER,
    TASK_SEARCH_TYPE_BASE,
    TASK_SEARCH_TYPE_BOARD,
} from "../constants";
import {post, get} from "../requests";
import moment from "moment";
import {pushAnalytics, replaceRuDuration, yandexTrackerIssueUrl} from "../helpers";
import {renderTimeViewClock} from "@mui/x-date-pickers/timeViewRenderers";
import {useTranslation} from "react-i18next";
import {useAtomValue, useSetAtom} from "jotai";
import {usersAtom, boardsAtom, resultGroupsAtom, myUserAtom, selectedUsersAtom, workLogsAtom} from "../jotai/atoms";
import {useDateFormatter, useLoader, useMessage} from "../hooks";
import {DialogsIssueSearch} from "./Dialogs/IssueSearch";
import Box from "@mui/material/Box";

function CreateWorkLogFromRecommendationDialog({isOpen, onClose, issue, initialDate}) {
    const {t} = useTranslation();

    const {showSuccess, showError} = useMessage();
    const {startLoading, endLoading} = useLoader();
    const {formatDateExact} = useDateFormatter();

    const users = useAtomValue(usersAtom);
    const boards = useAtomValue(boardsAtom);
    const resultGroups = useAtomValue(resultGroupsAtom);
    const myUser = useAtomValue(myUserAtom);
    const selectedUsers = useAtomValue(selectedUsersAtom);
    const setWorkLogs = useSetAtom(workLogsAtom);

    const [userIdentity, setUserIdentity] = useState(null);
    const [issueDate, setIssueDate] = useState(initialDate || moment());

    useEffect(() => {
        if (resultGroups.includes(RESULT_GROUP_WORKER)) {
            if (myUser.isAdmin) {
                if (selectedUsers.length === 1) {
                    setUserIdentity(selectedUsers[0]);
                    return;
                }
            }
        }

        setUserIdentity(myUser.value);
    }, [myUser, resultGroups, selectedUsers]);

    const [taskSearchType, setTaskSearchType] = useState(TASK_SEARCH_TYPE_BASE);
    const [taskSearchDialogState, setTaskSearchDialogState] = useState(false);
    const [tasks, setTasks] = useState([]);
    const [taskSearch, setTaskSearch] = useState(issue ? issue.key : "");

    const [taskBoard, setTaskBoard] = useState(0);
    const [sprints, setSprints] = useState([]);
    const [sprint, setSprint] = useState(0);

    const [newIssue, setNewIssue] = useState(issue ? {key: issue.key, title: `${issue.key}: ${issue.summary}`} : {key: "", title: ""});

    const taskSearchTimer = useRef(0);

    const [withComment, setWithComment] = useState(false);

    const [issuePlaceholder, setIssuePlaceholder] = useState("");

    // Pre-fill task search when dialog opens with issue
    useEffect(() => {
        if (isOpen && issue) {
            setTaskSearch(issue.key);
            setNewIssue({key: issue.key, title: `${issue.key}: ${issue.summary}`});
        } else if (isOpen && !issue) {
            setTaskSearch("");
            setNewIssue({key: "", title: ""});
        }
    }, [isOpen, issue]);

    // Set initial date when dialog opens
    useEffect(() => {
        if (isOpen) {
            const date = initialDate || moment();
            const nowDate = moment();
            date.set('hour', nowDate.hour());
            date.set('minute', nowDate.minute());
            setIssueDate(date);
        }
    }, [isOpen, initialDate]);

    const handleSubmit = (e) => {
        e.preventDefault();

        const data = new FormData(e.currentTarget);

        const realIssueKey = newIssue.key;

        if (!realIssueKey) {
            return showError(t('notifications:please_choose_issue'));
        }

        const request = {
            userIdentity: String(userIdentity),
            myUser: userIdentity === myUser.value,
            issueKey: realIssueKey,
            duration: replaceRuDuration(data.get("duration")),
            comment: data.get("comment"),
            date: issueDate.format()
        };

        if (withComment) {
            const rawIssueComment = data.get("issueComment") || data.get("issueComment")?.trim();

            if (rawIssueComment === "") {
                request.issueComment = data.get("comment");
            } else {
                request.issueComment = rawIssueComment;
            }
        }

        if (request.duration.includes('-')) {
            return showError(t('notifications:unable_to_use_negative_time'));
        }

        startLoading();

        post("/api/v1/work_logs", request).then(response => {
            const workLog = response.data;

            if (selectedUsers.length > 0 && !selectedUsers.includes(String(workLog.createdById))) {
                showSuccess(t('notifications:work_log_created_with_restrictions'));
            } else {
                showSuccess(t('notifications:work_log_created'));
                setWorkLogs(prev => [...prev, workLog]);
            }

            onClose();

            pushAnalytics('workLogCreated', {
                withComment: withComment
            });
        }).catch(showError).finally(endLoading);
    };

    const handleTaskSearch = (e) => {
        const value = e.target.value.trim();

        if (value.length >= 3) {
            setTaskSearch(value);
        }
    };

    useEffect(() => {
        clearTimeout(taskSearchTimer.current);

        if (taskSearch.length >= 3) {
            taskSearchTimer.current = setTimeout(() => {
                startLoading();

                post("/api/v1/filter_tasks", {
                    query: taskSearch
                }).then(response => {
                    setTasks(response.data);
                    setTaskSearchDialogState(true);
                }).catch(showError).finally(endLoading);
            }, 350);
        }

    }, [taskSearch]);

    const taskFilter = () => {
        const taskFilterBase = () => {
            return <Grid size={{xs: 12}}>
                <TextField 
                    label={t('components:create_work_log_dialog.fields.search.label')} 
                    variant="outlined"
                    fullWidth 
                    onChange={e => {
                        const value = e.target.value.trim();
                        setTaskSearch(value);
                        if (value.length >= 3) {
                            handleTaskSearch(e);
                        }
                    }}
                    value={taskSearch}
                />
            </Grid>
        };

        const taskFilterBoards = () => {
            return <Grid size={{xs: 12}}>
                <Grid container spacing={2}>
                    <Grid size={{xs: 12}}>
                        <CustomAutocomplete
                            onChange={(e, newValue) => setTaskBoard(newValue ? newValue.value : 0)}
                            value={boards.find(u => u.value === taskBoard) || null}
                            options={boards}
                            label={t('components:create_work_log_dialog.fields.board.label')}
                        />
                    </Grid>
                    {sprints.length > 0 && <Grid size={{xs: 12}}>
                        <CustomAutocomplete
                            onChange={(e, newValue) => setSprint(newValue ? newValue.value : 0)}
                            value={sprints.find(u => u.value === sprint) || null}
                            options={sprints}
                            label={t('components:create_work_log_dialog.fields.sprint.label')}
                            renderOption={(props, option) => {
                                const {key, ...optionProps} = props;

                                return <Box
                                    key={key}
                                    component="li"
                                    {...optionProps}
                                >{t('components:create_work_log_dialog.sprint_name', {
                                    name: option.properties.name,
                                    startDate: formatDateExact(option.properties.startDate),
                                    endDate: formatDateExact(option.properties.endDate)
                                })}</Box>
                            }}
                        />
                    </Grid>}
                </Grid>
            </Grid>
        };

        return <Grid size={{xs: 12}}>
            <Grid container spacing={2}>
                {boards.length > 0 && <Grid size={{xs: 12}}>
                    <FormControl fullWidth>
                        <FormLabel>{t('components:create_work_log_dialog.issues_search_type')}</FormLabel>
                        <RadioGroup row
                                    value={taskSearchType}
                                    onChange={(e, newValue) => setTaskSearchType(newValue)}
                        >
                            <FormControlLabel value={TASK_SEARCH_TYPE_BASE} control={<Radio/>}
                                              label={t('components:create_work_log_dialog.fields.search_type.values.BASE')}/>
                            <FormControlLabel value={TASK_SEARCH_TYPE_BOARD} control={<Radio/>}
                                              label={t('components:create_work_log_dialog.fields.search_type.values.BOARD')}/>
                        </RadioGroup>
                    </FormControl>
                </Grid>}
                {taskSearchType === TASK_SEARCH_TYPE_BASE && taskFilterBase()}
                {taskSearchType === TASK_SEARCH_TYPE_BOARD && taskFilterBoards()}
                <Grid size={{xs: 12}}>
                    {newIssue.key === "" && <div>
                        {taskSearch.length <= 2 && t('notifications:minimum_search_term_is_3_symbols')}
                    </div>}
                    {newIssue.key !== "" && <div>
                        {t('components:create_work_log_dialog.selected_issue')}<br/>
                        <Link href={yandexTrackerIssueUrl(newIssue.key)} target="_blank" rel="nofollow noreferer">
                            {newIssue.title}
                        </Link>
                    </div>}
                </Grid>
            </Grid>
        </Grid>
    };

    useEffect(() => {
        if (taskBoard > 0 && sprint > 0) {
            startLoading();

            get(`/api/v1/boards/${taskBoard}/sprints/${sprint}/tasks`)
                .then(response => {
                    if (response.data.length === 0) {
                        return showError(t('notifications:issues_not_found'));
                    }

                    setTasks(response.data);
                    setTaskSearchDialogState(true);
                }).catch(showError).finally(endLoading);
        }
    }, [sprint]);

    useEffect(() => {
        if (taskBoard > 0) {
            startLoading();

            get(`/api/v1/boards/${taskBoard}/sprints`)
                .then(response => {
                    if (response.data.length === 0) {
                        showError(t('notifications:no_sprints_found_on_board'));
                    }

                    setSprints(response.data.map(sprint => ({
                        value: sprint.id,
                        properties: {
                            name: sprint.attributes.name,
                            startDate: moment(sprint.attributes.startDate),
                            endDate: moment(sprint.attributes.endDate)
                        },
                        label: sprint.attributes.name
                    })));
                }).catch(showError).finally(endLoading);
        }
    }, [taskBoard]);

    useEffect(() => {
        if (!isOpen) {
            setNewIssue({key: "", title: ""});
            setTaskBoard(0);
            setSprints([]);
            setSprint(0);
            setTaskSearchType(TASK_SEARCH_TYPE_BASE);
            setTaskSearch("");
        }
    }, [isOpen]);

    return <Dialog open={isOpen} onClose={() => onClose()} maxWidth="md" fullWidth>
        <DialogsIssueSearch state={taskSearchDialogState} handleClose={() => setTaskSearchDialogState(false)}
                            tasks={tasks}
                            onSelect={task => {
                                setTaskSearchDialogState(false);
                                setNewIssue({key: task.id, title: `${task.id}: ${task.attributes.summary}`})
                            }}/>

        <form onSubmit={(e) => handleSubmit(e)}>
            <DialogTitle>{t('components:create_work_log_dialog.title')}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{paddingTop: 1}}>
                    <Grid size={{xs: 12, md: 6}}>
                        <FormControl fullWidth>
                            <DatePicker
                                label={t('components:create_work_log_dialog.fields.date.label')}
                                value={issueDate}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        const currentTime = issueDate;
                                        newValue.set('hour', currentTime.hour());
                                        newValue.set('minute', currentTime.minute());
                                        setIssueDate(newValue);
                                    }
                                }}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={{xs: 12, md: 6}}>
                        <FormControl fullWidth>
                            <TimePicker
                                label={t('components:create_work_log_dialog.fields.time.label')}
                                value={issueDate}
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                }}
                                onChange={(newValue) => {
                                    setIssueDate(newValue);
                                }}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={{xs: 12}}>
                        <FormControl fullWidth>
                            <CustomAutocomplete
                                onChange={(e, newValue) => setUserIdentity(newValue ? newValue.value : null)}
                                disabled={resultGroups.includes(RESULT_GROUP_WORKER) || !myUser.isAdmin}
                                value={users.find(u => String(u.value) === String(userIdentity)) || null}
                                options={users}
                                label={t('components:create_work_log_dialog.fields.user.label')}
                            />
                        </FormControl>
                    </Grid>
                    {taskFilter()}
                    <Grid size={{xs: 12}}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label={t('components:create_work_log_dialog.fields.duration.label')}
                            fullWidth
                            name="duration"
                            variant="standard"
                            defaultValue=""
                        />
                    </Grid>
                    <Grid size={{xs: 12}}>
                        <TextField
                            label={t('components:create_work_log_dialog.fields.comment.label')}
                            multiline
                            fullWidth
                            rows={5}
                            defaultValue=""
                            name="comment"
                            variant="standard"
                            onChange={(e) => setIssuePlaceholder(e.target.value)}
                        />
                    </Grid>
                    {String(userIdentity) === String(myUser.value) && !myUser.isReadOnly && <Grid size={{xs: 12}}>
                        <FormGroup>
                            <FormControlLabel control={<Checkbox/>}
                                              label={t('components:create_work_log_dialog.fields.with_comment.label')}
                                              checked={withComment} onChange={(e) => setWithComment(e.target.checked)}/>
                        </FormGroup>
                    </Grid>}
                    {(String(userIdentity) === String(myUser.value) && withComment) && <Grid size={{xs: 12}}>
                        <TextField
                            label={t('components:create_work_log_dialog.fields.issue_comment.label')}
                            multiline
                            fullWidth
                            rows={5}
                            placeholder={issuePlaceholder}
                            name="issueComment"
                            variant="standard"
                            focused={issuePlaceholder !== ""}
                        />
                    </Grid>}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={() => onClose()} color="warning">{t('common:button.cancel')}</Button>
                <Button type="submit" color="success">{t('common:button.create')}</Button>
            </DialogActions>
        </form>
    </Dialog>
}

export default CreateWorkLogFromRecommendationDialog;

